import { toast } from 'sonner';

/**
 * Fallback clipboard copy using execCommand (works in iframes)
 * Creates a temporary textarea, copies text, then removes it
 */
function fallbackCopyToClipboard(text: string): boolean {
  const textArea = document.createElement('textarea');
  textArea.value = text;

  // Make it readonly to prevent mobile keyboard from showing
  textArea.setAttribute('readonly', '');

  // Position off-screen but ensure it's visible to the browser
  textArea.style.position = 'absolute';
  textArea.style.left = '-9999px';
  textArea.style.top = '0';

  // Ensure it's not hidden or has zero size
  textArea.style.width = '1px';
  textArea.style.height = '1px';
  textArea.style.padding = '0';
  textArea.style.border = 'none';
  textArea.style.outline = 'none';
  textArea.style.boxShadow = 'none';

  document.body.appendChild(textArea);

  // Select the text content
  textArea.focus();
  textArea.select();

  // iOS fix - use setSelectionRange
  textArea.setSelectionRange(0, text.length);


  let success = false;
  try {
    success = document.execCommand('copy');
  } catch (error) {
    success = false;
  }

  document.body.removeChild(textArea);
  return success;
}

/**
 * Safely copy text to clipboard with iframe-friendly fallback
 * Shows appropriate toast messages for success/failure
 *
 * When running inside an iframe (e.g., Cortex integration), the browser
 * blocks clipboard access due to permissions policy. This utility uses
 * execCommand as a fallback which works in iframes.
 */
export async function copyToClipboard(
  text: string,
  successMessage = 'Copied to clipboard',
  failureMessage = 'Copy not available'
): Promise<boolean> {

  // Try modern Clipboard API first
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
      return true;
    } catch (error) {
      // Clipboard API blocked, try fallback
    }
  }

  // Fallback to execCommand (works in iframes)
  const success = fallbackCopyToClipboard(text);
  if (success) {
    toast.success(successMessage);
  } else {
    toast.error(failureMessage);
  }
  return success;
}
